import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import type { UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import { toast } from '@/components/ui/use-toast';
import type { Selectable } from '@/lib/pages/forms/components/Select';

import {
  glacierAgenciesApiListWritingCompanyOptions,
  glacierPolicyApiDownloadErpWorksheetsMutation,
  glacierPolicyApiGetLatestTransactionSummaryOptions,
  glacierUsersApiCurrentUserOptions,
  glacierUsersApiRelatedUsersOptions,
} from '@/lib/heyapi/@tanstack/react-query.gen';

import type {
  AgencyWithStatesSchema,
  CurrentUserSchema,
  GlacierAgenciesApiListWritingCompanyResponse,
  InvoicePreference,
  RelatedAgentsSchema,
  WritingCompanySchema,
} from '../heyapi/types.gen';
import { ErpWorksheetEnum } from '../heyapi/types.gen';

export type CurrentUserData = {
  currentUser: UseQueryResult<CurrentUserSchema, Error>;
  agenciesSelectable: Array<Selectable<string>>;
  authorizedStates: Record<string, AgencyWithStatesSchema['authorizedStates']>;
};

export function useCurrentUserData(): CurrentUserData {
  const currentUser = useQuery(glacierUsersApiCurrentUserOptions());

  const [agencies, authorizedStates] = useMemo(() => {
    const data = currentUser.data?.agentData;
    const uwData = currentUser.data?.underwriterData;
    const selectableAgency = new Set<{
      id: string;
      name: string;
      invoicePreference: InvoicePreference;
    }>();
    const authorized: Record<
      string,
      AgencyWithStatesSchema['authorizedStates']
    > = {};

    if (data) {
      data.forEach((agencyWithStates) => {
        selectableAgency.add({
          id: agencyWithStates.agencyId!,
          name: agencyWithStates.name,
          invoicePreference: agencyWithStates.invoicePreference,
        });

        authorized[agencyWithStates.agencyId!] =
          agencyWithStates.authorizedStates;
      });
    }

    if (uwData) {
      uwData.forEach((agency) => {
        selectableAgency.add({
          id: agency.agencyId!,
          name: agency.name,
          invoicePreference: agency.invoicePreference,
        });

        authorized[agency.agencyId!] = agency.authorizedStates;
      });
    }
    return [[...selectableAgency], authorized];
  }, [currentUser.data]);

  const agenciesSelectable: Array<Selectable<string>> = useMemo(() => {
    const agenciesData = agencies.map(({ id, name, invoicePreference }) => ({
      label: name,
      value: id!,
      invoicePreference,
    }));

    const orderAgencies = agenciesData.toSorted((a, b) => {
      return a.label.localeCompare(b.label);
    });

    return orderAgencies;
  }, [agencies]);

  return { currentUser, agenciesSelectable, authorizedStates };
}

export type RelatedUsersData = {
  relatedUsers: UseQueryResult<RelatedAgentsSchema, Error>;
  usersByAgencySelectable: Record<string, Array<Selectable<string>>>;
  agentsByAgencySelectable: Record<string, Array<Selectable<string>>>;
};

export function useRelatedUsersData(): RelatedUsersData {
  const relatedUsers = useQuery(glacierUsersApiRelatedUsersOptions());

  const usersByAgencySelectable: Record<string, Selectable<string>[]> =
    useMemo(() => {
      const { data } = relatedUsers;
      if (!data) return {};

      return Object.entries(data.agency).reduce(
        (acc, [agency, users]) => {
          acc[agency] = users.map((user) => {
            return {
              label: `${user.firstName} ${user.lastName}`,
              value: `${user.id!}`,
            };
          });
          return acc;
        },
        {} as Record<string, Selectable<string>[]>
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: review deps in future refactor
    }, [relatedUsers.data]);

  const agentsByAgencySelectable: Record<string, Selectable<string>[]> =
    useMemo(() => {
      const { data } = relatedUsers;
      if (!data) return {};

      return Object.entries(data.agency).reduce(
        (acc, [agency, users]) => {
          acc[agency] = users
            .filter((u) => u.role === 'agent')
            .map((user) => {
              return {
                label: `${user.firstName} ${user.lastName}`,
                value: `${user.id!}`,
              };
            });
          return acc;
        },
        {} as Record<string, Selectable<string>[]>
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: review deps in future refactor
    }, [relatedUsers.data]);

  return { relatedUsers, usersByAgencySelectable, agentsByAgencySelectable };
}

export type WritingCompaniesData = {
  writingCompanies: UseQueryResult<
    GlacierAgenciesApiListWritingCompanyResponse,
    Error
  >;
  writingCompaniesSelectable: Array<Selectable<string>>;
  getWritingCompanyByUUID: (uuid: string) => WritingCompanySchema | null;
};

export function useWritingCompaniesData(): WritingCompaniesData {
  const writingCompanies = useQuery(
    glacierAgenciesApiListWritingCompanyOptions()
  );

  const writingCompaniesSelectable = useMemo(() => {
    const { data } = writingCompanies;
    if (!data) return [];

    return writingCompanies.data?.map(({ uuid, name, shortName }) => ({
      label: `${name} (${shortName?.toUpperCase()})`,
      value: uuid!,
    }));
  }, [writingCompanies]);

  const getWritingCompanyByUUID = useCallback(
    (uuid: string) => {
      const { data } = writingCompanies;
      if (!data) return null;

      const writingCompany = data.find((company) => company.uuid === uuid);
      return writingCompany || null;
    },
    [writingCompanies]
  );

  return {
    writingCompanies,
    writingCompaniesSelectable,
    getWritingCompanyByUUID,
  };
}

export const useCustomSearchParams = () => {
  const [search, setSearch] = useSearchParams();
  const searchAsObject = Object.fromEntries(new URLSearchParams(search));

  return [searchAsObject, setSearch] as const;
};

// Custom hook for ERP Estimate Worksheet functionality
export const useERPEstimateWorksheet = (policyId: string) => {
  const [showERPButton, setShowERPButton] = useState(false);

  // Use the generated hook to get the summary
  const summaryQuery = useQuery({
    ...glacierPolicyApiGetLatestTransactionSummaryOptions({
      path: { account_id: policyId },
    }),
    enabled: Boolean(policyId),
  });

  // Use the generated hook for download mutation
  const erpWorksheetMutation = useMutation({
    ...glacierPolicyApiDownloadErpWorksheetsMutation(),
    onSuccess(data) {
      if (!data) {
        throw new Error('No data received');
      }

      const url = URL.createObjectURL(data);
      window.open(url, '_blank');
      // Revoke after a short delay to allow tab to load
      setTimeout(() => URL.revokeObjectURL(url), 500);

      toast({
        title: 'Success',
        description: 'ERP Estimate Worksheet downloaded successfully!',
      });
    },
    onError: (error) => {
      console.error('Error downloading ERP worksheet:', error);
      toast({
        title: 'Error',
        description:
          'Failed to download ERP Estimate Worksheet. Please try again.',
      });
    },
  });

  useEffect(() => {
    if (
      summaryQuery.data?.availableActions?.includes('download_erp_worksheet')
    ) {
      setShowERPButton(true);
    } else {
      setShowERPButton(false);
    }
  }, [summaryQuery.data]);

  const downloadERPWorksheet = () => {
    erpWorksheetMutation.mutate({
      path: {
        account_id: policyId,
        worksheet_enum: ErpWorksheetEnum.ERP_ESTIMATE,
      },
    });
  };

  return {
    showERPButton,
    isDownloading: erpWorksheetMutation.isPending,
    downloadERPWorksheet,
  };
};
